import { z } from 'zod';

export const zAppVersionPlatform = z.literal(['ios', 'android']);

export const appleAppStoreInfoSchema = z.object({
  appStoreVersion: z.string().nullable().default(null),
  appStoreUrl: z.string().nullable().default(null),
  releaseNotes: z.string().nullable().default(null),
});
export type AppleAppStoreInfo = z.output<typeof appleAppStoreInfoSchema>;

export const appStoreUpdatePolicySchema = z.object({
  /**
   * Bundle identifier for the application (e.g., "com.example.app")
   */
  bundleId: z.string().min(1),
  /**
   * Platform type
   */
  platform: zAppVersionPlatform,
  /**
   * Native app version that triggers update banner to show.
   */
  targetNativeVersion: z.number().min(1).nullable().default(null),
  /**
   * Whether the update is required (forced) rather than optional.
   */
  isUpdateRequired: z.boolean().default(false),

  /**
   * The result of the lookupAppleAppStoreInfo action.
   */
  appleAppStoreInfo: appleAppStoreInfoSchema.nullable().default(null).optional(),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type AppStoreUpdatePolicy = z.output<typeof appStoreUpdatePolicySchema>;

// Helper
const toNumber = (value: unknown): number | null => {
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return typeof value === 'number' ? value : null;
};

// FIXME: Remove the use of z.preprocess()! It results in `any` type in Convex
export const appStoreUpdatePolicyStrictSchema = appStoreUpdatePolicySchema.extend({
  targetNativeVersion: z.preprocess(toNumber, z.number().min(1).nullable()),
});

// Convex schema extension
export const appStoreUpdatePolicyCvxSchema = appStoreUpdatePolicySchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
});
export type AppStoreUpdatePolicyCvx = z.output<typeof appStoreUpdatePolicyCvxSchema>;

/**
 * Response indicating whether an app update is available.
 * The type is a discriminated union based on the `isUpdateAvailable` property.
 */
export const appVersionCheckResponseSchema = z.discriminatedUnion('isUpdateAvailable', [
  z.object({
    /** Indicates that a native iOS or Android app update is available in the App Store or Play Store */
    isUpdateAvailable: z.literal(true),
    /**
     * Indicates if the app update is mandatory or optional.
     * Mandatory updates will block the user from using the app until the update is installed.
     */
    isUpdateRequired: z.boolean(),
    /** The App Store information for the app, if available. Will be null for Android. */
    appleAppStoreInfo: appleAppStoreInfoSchema.nullable(),
  }),
  z.object({
    /** Indicates that no app update is available */
    isUpdateAvailable: z.literal(false),
    /** Always false when no update is available */
    isUpdateRequired: z.literal(false),
    /** No App Store information when no update is available */
    appleAppStoreInfo: appleAppStoreInfoSchema.nullable(),
  }),
]);
